/**
 * Whether the email address and the phone number already have an enquiry
 * against them, asked while the form is being filled in.
 *
 * The point is what it saves: without it, somebody who wrote in last month
 * fills in nine fields, presses Send, and only then learns the practitioner
 * already has them. The answer is owed either way — this just gives it under
 * the field it is about, at the moment they finish typing it.
 *
 * The two fields are asked about separately and independently. A combined
 * answer could only say "one of these is already on file" without saying
 * which, which is exactly the message this exists to avoid.
 *
 * Hand-rolled rather than a `useQuery`, for the same reason `usePublicEnquiryForm`
 * is: TanStack's provider belongs to the dashboard's tree and nothing here
 * renders inside it.
 *
 * ## What it does not do
 *
 * It never blocks and never speaks up about itself. A check that fails —
 * offline, a timeout, a 500, an endpoint an older backend does not serve —
 * leaves the field exactly as it was, silently. The submit is where the
 * question gets a binding answer, and a courtesy that turned into "we could
 * not check, so you cannot send" would be worse than not asking at all.
 */

import { useEffect, useMemo, useState } from "react";
import { checkPublicContact } from "../services/publicEnquiryService";
import type { PublicContactField } from "../services/publicEnquiryService";
import { fieldError } from "../publicEnquiryValidation";
import type { PublicEnquiryErrors } from "../publicEnquiryValidation";

/**
 * How long the typing has to stop before the question is asked.
 *
 * Long enough that an address is not asked about eight times on the way to
 * being typed, short enough that the answer arrives while the reader is still
 * looking at the field. It matters more here than in an ordinary search box:
 * every keystroke that reached the network would be one more contact detail
 * sent to a server that no session is behind.
 */
const SETTLE_MS = 600;

/**
 * One field's verdict — the message to show, or `""`.
 *
 * Called twice below, never in a loop: the fields are two, they are known at
 * compile time, and a hook per field is what keeps each one's request, its
 * timer and its abort to itself. A single effect over both would have to
 * cancel the phone's in-flight check every time the email changed.
 */
const useContactField = (
  handle: string,
  field: PublicContactField,
  value: string
): string => {
  const [message, setMessage] = useState("");

  useEffect(() => {
    // The verdict belonged to the value that has just been edited away. It is
    // withdrawn before anything else, so a stale "already registered" cannot
    // sit under an address that has since been corrected.
    setMessage("");

    const answer = value.trim();
    // Nothing worth asking about: no practitioner to ask, an empty field, or
    // one that does not yet pass its own rules. The last is the one that
    // matters — `r`, `ra`, `rav` are not addresses, and sending them upstream
    // would be three contact details handed to the server for no answer.
    if (!handle || !answer || fieldError(field, answer)) return;

    const controller = new AbortController();
    const timer = setTimeout(() => {
      checkPublicContact(handle, field, answer, controller.signal)
        .then((duplicate) => {
          if (!controller.signal.aborted) setMessage(duplicate);
        })
        // Deliberately silent, and not logged either: see the note at the top.
        .catch(() => {});
    }, SETTLE_MS);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [handle, field, value]);

  return message;
};

/**
 * Both verdicts, keyed the way the form's own errors are — so the control can
 * show whichever of the two applies without caring where it came from.
 *
 * Empty is the ordinary case: nothing asked yet, nothing found, or a check
 * that could not be made.
 */
export function usePublicContactCheck(
  handle: string,
  email: string,
  phone: string
): PublicEnquiryErrors {
  const emailTaken = useContactField(handle, "email", email);
  const phoneTaken = useContactField(handle, "phone", phone);

  return useMemo(() => {
    const found: PublicEnquiryErrors = {};
    if (emailTaken) found.email = emailTaken;
    if (phoneTaken) found.phone = phoneTaken;
    return found;
  }, [emailTaken, phoneTaken]);
}

export default usePublicContactCheck;
